import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    Typography,
} from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import Divider from '@material-ui/core/Divider'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowForward } from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useStores } from '../../../hooks/use-stores'
import { joinPath } from '../../../utils/helpers'
import NumberFormat from 'react-number-format'
import PageTitle from '../../../components/PageTitle'
import { currencyFormatter } from '../../shoppingboss/components/StyledValueWithCurrency'
import InputAdornment from '@material-ui/core/InputAdornment'
import { Alert } from '@material-ui/lab'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import withStyles from '@material-ui/core/styles/withStyles'
import TextField from '@material-ui/core/TextField'
import ZelleDepositInstructionsPanel from '../../../components/Wallet/ZelleDepositInstructionsPanel'

const CssTextField = withStyles({
    root: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: '#007BFF' },
            '&:hover fieldset': { borderColor: '#007BFF' },
            '&.Mui-focused fieldset': { borderColor: '#007BFF' },
        },
    },
})(TextField)

function NumberFormatCustom(props) {
    const { inputRef, onChange, ...other } = props
    return (
        <NumberFormat
            {...other}
            decimalScale={2}
            thousandSeparator={true}
            getInputRef={inputRef}
            type="text"
            onValueChange={(values) => onChange({ target: { value: values.value } })}
        />
    )
}

const useStyles = makeStyles((theme) => ({
    root: { display: 'flex', flexDirection: 'column', flex: 1 },
    paper: { background: '#fff', marginBottom: 20, padding: theme.spacing(2), flexGrow: 1 },
    divider: { width: '100%' },
    rightAlign: { textAlign: 'right' },
    icons: { gap: theme.spacing(3), flexBasis: 'initial' },
    avatar: { width: 75, height: 75 },
    nextButton: { marginTop: '24px' },
}))

const ZELLE_MIN = 100
const ZELLE_MAX = 999.99

export default observer(function AddEWalletZelle({ parentMatch, walletMatch, depositEligibility }) {
    const classes = useStyles()
    const { t } = useTranslation('wallet')
    const history = useHistory()
    const { shoppingBossStore, authStore, userBillingInfoStore } = useStores()
    const { currentUser } = authStore
    const { eWalletCanAddOneTime } = shoppingBossStore
    const accountId = currentUser?.userID ?? ''
    const accountId8 = String(accountId).slice(-8).padStart(8, '0')

    const [step, setStep] = useState(1)
    const [depositAmount, setDepositAmount] = useState('')
    const [acknowledge, setAcknowledge] = useState(false)

    useEffect(() => {
        void shoppingBossStore.fetchUserBalance()
    }, [])

    const minimum = ZELLE_MIN
    const maximum = useMemo(() => Math.min(eWalletCanAddOneTime ?? 0, ZELLE_MAX), [eWalletCanAddOneTime])
    const amountNum = useMemo(() => (depositAmount === '' ? 0 : parseFloat(depositAmount)), [depositAmount])
    const canNext = amountNum >= minimum && amountNum <= maximum
    const canConfirm = acknowledge

    const totalCents = Math.round(amountNum * 100)
    const feeCents = Math.round(totalCents * 0.01) + 15
    const depositCents = totalCents - feeCents

    const { makingZelleDeposits: submitting, error: billingError } = userBillingInfoStore
    const walletBackPath = walletMatch?.url && walletMatch.url !== parentMatch?.url
        ? walletMatch.url
        : joinPath(parentMatch?.url ?? '', 'wallet')
    const eWalletTransactionsPath = joinPath(walletBackPath, 'transactions')

    const handleDepositAmountChange = (e) => {
        const val = e.target.value
        if (val !== '' && isNaN(Number(val))) return
        setDepositAmount(val)
    }

    const handleInstructionsClose = () => {
        history.push(eWalletTransactionsPath)
    }

    const handleConfirm = async () => {
        if (depositEligibility?.canInitiateDeposit === false) {
            return
        }
        await userBillingInfoStore.makeZelleDeposit(depositAmount)
        if (!userBillingInfoStore.error) {
            void shoppingBossStore.fetchUserBalance()
            setStep(3)
        }
    }

    const lightTheme = createTheme({ palette: { type: 'light' } })

    if (depositEligibility?.canInitiateDeposit === false) {
        return <Alert variant="filled" color="error">{depositEligibility.message || t('add_money.deposit_eligibility_error')}</Alert>
    }

    if (step === 1) {
        return (
            <>
                <PageTitle>{t('add_money.bank.title')}</PageTitle>
                <Box display="flex" flexDirection="column" alignItems="center" className={classes.root}>
                    <Box display="flex" alignItems="center" className={classes.icons}>
                        <Avatar
                            className={classes.avatar}
                            src="https://resources.lifeinfoapp.com/public/icon/wallet/giftcards.png"
                            variant="rounded"
                        />
                        <ArrowForward />
                        <Avatar
                            className={classes.avatar}
                            src="https://resources.lifeinfoapp.com/public/icon/wallet/ewallet.png"
                            variant="rounded"
                        />
                    </Box>
                    <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                        {t('add_money.general.enter_amount')}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 20, marginTop: 16, width: '100%', maxWidth: 400 }}>
                        <CssTextField
                            type="number"
                            variant="outlined"
                            value={depositAmount}
                            onChange={handleDepositAmountChange}
                            InputProps={{
                                inputComponent: NumberFormatCustom,
                                startAdornment: (
                                    <InputAdornment position="start">$</InputAdornment>
                                ),
                            }}
                            fullWidth
                        />
                        <Box flexDirection="column" whiteSpace="nowrap">
                            <div>{t('add_money.general.minimum', { amount: `$${minimum.toFixed(2)}` })}</div>
                            <div>{t('add_money.general.maximum', { amount: `$${Number(maximum).toFixed(2)}` })}</div>
                        </Box>
                    </Box>
                    <Button
                        className={classes.nextButton}
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!canNext}
                        onClick={() => setStep(2)}
                        style={{ maxWidth: 400 }}
                    >
                        NEXT
                    </Button>
                </Box>
            </>
        )
    }

    if (step === 2) {
        return (
            <>
                <PageTitle>{t('add_money.bank.titleConfirmation')}</PageTitle>
                <ThemeProvider theme={lightTheme}>
                    <Paper classes={{ root: classes.paper }}>
                        {billingError && (
                            <Alert severity="error" style={{ marginBottom: 16 }}>
                                {billingError}
                            </Alert>
                        )}
                        <Typography variant="subtitle1" color="primary" style={{ fontWeight: 600, marginBottom: 8 }}>
                            {t('add_money.zelle_confirm_subtitle')}
                        </Typography>
                        <Grid container>
                            <Grid item xs={8}>
                                <Typography variant="subtitle1">{t('add_money.confirm.zelle_deposit')}</Typography>
                            </Grid>
                            <Grid item xs={4} className={classes.rightAlign}>
                                <b>{currencyFormatter(totalCents)}</b>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="subtitle1">{t('add_money.confirm.zelle_processing_fee')}</Typography>
                            </Grid>
                            <Grid item xs={4} className={classes.rightAlign}>
                                <b>{currencyFormatter(feeCents)}</b>
                            </Grid>
                        </Grid>
                        <Divider className={classes.divider} style={{ margin: '16px 0' }} />
                        <Typography variant="body2" component="ul" style={{ paddingLeft: 20 }}>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                <li key={i}>{i === 7
                                    ? <b>{t(`add_money.zelle_terms_line${i}`)}</b>
                                    : t(`add_money.zelle_terms_line${i}`)
                                }</li>
                            ))}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={acknowledge}
                                    onChange={() => setAcknowledge(!acknowledge)}
                                />
                            }
                            label={t('add_money.zelle_confirm_acknowledge')}
                            style={{ marginTop: 16 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!canConfirm || submitting}
                            onClick={handleConfirm}
                            style={{ marginTop: 16 }}
                        >
                            {submitting ? '...' : 'CONFIRM'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            disabled={submitting}
                            onClick={() => history.push(walletBackPath)}
                            style={{ marginTop: 12 }}
                        >
                            {t('add_money.zelle_confirmation_cancel')}
                        </Button>
                    </Paper>
                </ThemeProvider>
            </>
        )
    }

    return (
        <>
            <PageTitle>{t('add_money.zelle_complete_title')}</PageTitle>
            <ThemeProvider theme={lightTheme}>
                <ZelleDepositInstructionsPanel
                    t={t}
                    accountId8={accountId8}
                    onClose={handleInstructionsClose}
                    billingError={billingError}
                />
            </ThemeProvider>
        </>
    )
})
